import { useEffect, useRef, useState } from 'react';

// How long the outgoing list takes to fade (matches the `.is-leaving` transition).
export const LEAVE_MS = 180;
const LIMIT = 8;
const recentOf = projects => projects.filter(p => p.status === 'active').slice(0, LIMIT);

// The sidebar's recent projects across a profile switch. What is on screen belongs to one
// profile: when another becomes active, its list leaves (`leaving`) while the new profile's
// projects load; once they are in and the fade has played, they replace it under a new
// `key`, so their staggered entrance plays. A profile without projects closes the block
// (`closing`) instead. Within one profile, updates land in place.
export default function useRecentProjects(projects, scope, ready) {
  const [state, setState] = useState(() => ({ key: scope, items: ready ? recentOf(projects) : [], leaving: false, closing: false }));
  const shownScope = useRef(scope);
  const leftAt = useRef(0);

  useEffect(() => {
    // The shown profile (again, if a switch was undone before the other one loaded).
    if (scope === shownScope.current) {
      if (ready) {
        leftAt.current = 0;
        setState(previous => ({ ...previous, items: recentOf(projects), leaving: false }));
      }
      return undefined;
    }
    if (!leftAt.current) {
      leftAt.current = Date.now();
      setState(previous => ({ ...previous, leaving: true }));
    }
    if (!ready) return undefined;
    const timer = setTimeout(() => {
      const items = recentOf(projects);
      shownScope.current = scope;
      leftAt.current = 0;
      setState(previous => ({ key: scope, items, leaving: false, closing: !items.length && previous.items.length > 0 }));
    }, Math.max(0, LEAVE_MS - (Date.now() - leftAt.current)));
    return () => clearTimeout(timer);
  }, [projects, scope, ready]);

  // The emptied block fades its title out before it goes.
  useEffect(() => {
    if (!state.closing) return undefined;
    const timer = setTimeout(() => setState(previous => ({ ...previous, closing: false })), LEAVE_MS);
    return () => clearTimeout(timer);
  }, [state.closing]);

  return state;
}
